import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, getCampaignSource } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  buildShareText, CHANNEL_URLS, openShareUrl,
  pickInstagramMode, shareInstagramNative, copyForInstagram,
  nativeShareOrCopy, copyLink, copyMessageAndLink,
  shareWithCopyThenOpen, isMobileUA,
} from "@/lib/share";
import { ChannelIcons } from "@/components/SocialIcons";

// Ordered list drives the icon grid layout.
const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp" },
  { key: "telegram", label: "Telegram" },
  { key: "facebook", label: "Facebook" },
  { key: "twitter",  label: "X" },
  { key: "instagram", label: "Instagram" },
  { key: "email",    label: "Email" },
  { key: "sms",      label: "SMS",       mobileOnly: true },
];

export default function PassItOn({ open, onOpenChange, message, incomingRef = null }) {
  const { requireAuth } = useAuth();
  const [shareId, setShareId] = useState(null);
  const [instagramPanel, setInstagramPanel] = useState(false);

  useEffect(() => {
    if (!open || !message) return;
    setInstagramPanel(false);
    requireAuth(async () => {
      try {
        // Propagate the INCOMING referral (?ref=...) as this share's
        // `parent_ref` so the propagation chain stays intact even when
        // the current viewer has not (yet) voiced. The backend uses
        // this chain to resolve the nearest VOICED ancestor at voice
        // finalisation time (see `_finalise_voice_from_payment`).
        const r = await api.post(`/messages/${message.id}/share`,
          { channel: "pending",
            parent_ref: incomingRef || null,
            source: getCampaignSource() });
        setShareId(r.data.share_id);
      } catch (e) {
        // if not authed, will trigger modal
      }
    });
  }, [open, message, incomingRef]);  // eslint-disable-line

  const link = shareId
    ? `${window.location.origin}/message/${message?.id}?ref=${shareId}`
    : `${window.location.origin}/message/${message?.id}`;

  const shareText = buildShareText(message?.title);
  const compactText = buildShareText(message?.title, { compact: true });

  const logShare = (channelKey) => {
    if (!shareId || !message) return;
    api.post(`/messages/${message.id}/share`,
      { channel: channelKey, parent_ref: shareId, source: getCampaignSource() })
      .catch(() => {});
  };

  // IMPORTANT: window.open MUST fire synchronously from the click handler,
  // otherwise Safari/mobile browsers drop the popup. Analytics is fired
  // afterwards without await.
  const openChannel = (ch) => {
    if (ch.key === "instagram") {
      const { mode } = pickInstagramMode();
      if (mode === "native") {
        shareInstagramNative({ url: link, text: shareText, title: message?.title });
        logShare(ch.key);
      } else {
        setInstagramPanel(true);
      }
      return;
    }
    const textForChannel = ch.key === "twitter" ? compactText : shareText;
    if (ch.key === "facebook" || ch.key === "linkedin") {
      const opened = shareWithCopyThenOpen({
        platform: ch.key, url: link, text: shareText,
      });
      if (!opened) toast.error("Your browser blocked the share window.");
      logShare(ch.key);
      return;
    }
    const urlBuilder = CHANNEL_URLS[ch.key];
    if (!urlBuilder) return;
    const opened = openShareUrl(urlBuilder(link, textForChannel));
    if (!opened && ch.key !== "sms") {
      toast.error("Your browser blocked the share window.");
    }
    logShare(ch.key);
  };

  const doCopy = async () => {
    const ok = await copyLink(link);
    if (ok) logShare("copy");
  };

  const doCopyMessageAndLink = async () => {
    const ok = await copyMessageAndLink({ url: link, text: shareText });
    if (ok) logShare("copy_message");
  };

  const doNativeShare = async () => {
    const res = await nativeShareOrCopy({
      url: link, text: shareText, title: message?.title,
    });
    if (res.via === "native" || res.via === "clipboard") logShare("share");
  };

  const doInstagramCopy = async () => {
    const ok = await copyForInstagram({ url: link, text: shareText });
    if (ok) logShare("instagram");
  };

  const visibleChannels = CHANNELS.filter(
    (c) => !c.mobileOnly || isMobileUA()
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--oh-bg)] border oh-hairline rounded-none max-w-md p-0 max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain"
        data-testid="pass-modal"
      >
        {instagramPanel ? (
          <div className="px-6 sm:px-10 py-8 sm:py-10 pb-[max(2rem,env(safe-area-inset-bottom))]"
               data-testid="pass-instagram-panel">
            <DialogTitle className="oh-label">Ready for Instagram</DialogTitle>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl leading-[1.05] mt-4">
              Your MESSAGE is ready.
            </h2>
            <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
              Post it, add it to your Story, or send it in a DM.
            </p>

            <div className="mt-8 border oh-hairline p-5 text-sm leading-relaxed text-[color:var(--oh-ink)] whitespace-pre-line"
                 data-testid="pass-instagram-preview">
              {shareText}
              {"\n\n"}{link}
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <button onClick={doInstagramCopy}
                      className="btn-primary flex items-center justify-center gap-2"
                      data-testid="pass-instagram-copy">
                {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
                Copy text + link
              </button>
              <button onClick={() => setInstagramPanel(false)}
                      className="btn-ghost"
                      data-testid="pass-instagram-back">
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 sm:px-10 py-8 sm:py-10 pb-[max(2rem,env(safe-area-inset-bottom))]">
            <DialogTitle className="oh-label">Pass it on</DialogTitle>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl leading-[1.05] mt-4">
              Make it travel.
            </h2>
            <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
              Share with at least 3 people you know. The farther it travels, the louder it becomes.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-2">
              {visibleChannels.map((ch) => {
                const Icon = ChannelIcons[ch.key];
                return (
                  <button
                    key={ch.key}
                    onClick={() => openChannel(ch)}
                    className="flex flex-col items-center justify-center gap-2 py-4 border oh-hairline hover:bg-[color:var(--oh-bg-2)] transition-colors"
                    data-testid={`pass-${ch.key}`}
                  >
                    {Icon && <Icon className="w-5 h-5 text-[color:var(--oh-ink)]" />}
                    <span className="text-[10px] uppercase tracking-wider text-[color:var(--oh-ink)]">
                      {ch.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={doCopy}
                      className="btn-ghost flex items-center justify-center gap-2"
                      data-testid="pass-copy">
                {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
                Copy link
              </button>
              <button onClick={doCopyMessageAndLink}
                      className="btn-ghost flex items-center justify-center gap-2"
                      data-testid="pass-copy-message">
                {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
                Copy message + link
              </button>
            </div>

            <button onClick={doNativeShare}
                    className="btn-primary w-full mt-3 flex items-center justify-center gap-2"
                    data-testid="pass-native">
              {ChannelIcons.share && <ChannelIcons.share className="w-4 h-4" />}
              Share
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
